import { Box, styled } from '@mui/material';
import { memo, useMemo } from 'react';

import { ELayoutManagerMode } from '#shared';

import { widgetRegistry, type WidgetInstance } from '../../widgetManager';
import { useLayoutMode } from '../model';

const PREFIX = 'TSMED-ContainerWidget';

const containerWidgetClasses = {
  root: `${PREFIX}-root`,
  placeholder: `${PREFIX}-placeholder`,
  hoverZone: `${PREFIX}-hoverZone`,
  widgetWrapper: `${PREFIX}-widgetWrapper`,
};
const Root = styled(Box, {
  shouldForwardProp: prop =>
    prop !== '$isSelected' &&
    prop !== '$mode' &&
    prop !== '$isOver' &&
    prop !== '$isAwaitingPlacement' &&
    prop !== '$enableToolbarPointerEvents' &&
    prop !== '$isDragging',
})<{
  $isSelected?: boolean;
  $mode?: ELayoutManagerMode;
  $isOver?: boolean;
  $isAwaitingPlacement?: boolean;
  $enableToolbarPointerEvents?: boolean;
  $isDragging?: boolean;
}>(
  ({
    $isSelected,
    $mode,
    $isOver,
    $isAwaitingPlacement,
    $enableToolbarPointerEvents,
    $isDragging,
    theme: { palette, spacing },
  }) => ({
    [`&.${containerWidgetClasses.root}`]: {
      color: $isSelected ? palette.primary.contrastText : 'inherit',
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'center',
      borderRadius: spacing(1),
      cursor:
        $mode === ELayoutManagerMode.EDIT
          ? $isSelected
            ? 'pointer'
            : 'grab'
          : 'default',
      fontSize: '12px',
      textAlign: 'center',
      position: 'relative',
      border: `1px solid ${palette.mapBlue[300]}`,
      width: '100%',
      overflow: 'visible',
      height: '100%',
      transition: 'box-shadow 0.3s ease-in-out, border-color 0.2s ease-in-out',
      transform: $isSelected ? 'scale(0.95)' : 'scale(1)',
      opacity: $isDragging || $isSelected ? 0.7 : 1,
      // Overlay for pulsing shadow effect (sits above content)
      '&::before':
        $isOver || $isAwaitingPlacement
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: spacing(1),
              boxShadow:
                'inset 0 0 25px rgba(33, 150, 243, 0.5), inset 0 4px 18px rgba(33, 150, 243, 0.4)',
              pointerEvents: 'none',
              zIndex: 50,
            }
          : {},
      // Only show hover effects in EDIT mode
      '&:hover':
        $mode === ELayoutManagerMode.EDIT && !$isSelected
          ? {
              backgroundColor: `${palette.mapBlue?.[300] ?? palette.primary.main}10`,
              '& .delete-button': {
                opacity: 1,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: spacing(1),
                boxShadow:
                  'inset 0 0 25px rgba(33, 150, 243, 0.5), inset 0 4px 18px rgba(33, 150, 243, 0.4)',
                pointerEvents: 'none',
                zIndex: 50,
              },
            }
          : {},
    },
    [`& .${containerWidgetClasses.hoverZone}`]: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      zIndex: 5,
      cursor: 'inherit',
    },
    [`& .${containerWidgetClasses.placeholder}`]: {
      borderRadius: spacing(1),
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#999',
      fontSize: '14px',
      position: 'absolute',
      height: '100%',
      width: '100%',
      img: {
        borderRadius: spacing(1),
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
      },
    },
    [`& .${containerWidgetClasses.widgetWrapper}`]: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      pointerEvents: 'none',
      '& > *': {
        flexGrow: 1,
        minHeight: 0,
      },
      // Enable pointer events for the toolbar in settings mode
      ...($enableToolbarPointerEvents && {
        '& .TSMED-Toolbar-root': {
          pointerEvents: 'auto',
        },
      }),
    },
  }),
);

export interface BlockPlaceholderProps {
  widget: WidgetInstance;
  isSelected: boolean;
  /** Whether this item is currently being dragged (from parent sortable) */
  isDragging?: boolean;
  /** Whether something is being dragged over this item (from parent sortable) */
  isOver?: boolean;
}

/**
 * Presentational component for widget placeholders in EDIT mode.
 *
 * This is a pure presentational component - drag behavior is handled by
 * the parent Block component which uses useSortable. This follows the
 * dnd-kit recommended pattern of separating sortable logic from presentation.
 *
 * @see https://docs.dndkit.com/presets/sortable#drag-overlay
 */
export const BlockPlaceholder = memo(function BlockPlaceholder({
  widget,
  isSelected,
  isDragging = false,
  isOver = false,
}: BlockPlaceholderProps) {
  const {
    mode,
    setSelectedWidgetId,
    selectedWidgetId,
    setSelectedPaletteWidget,
    selectedPaletteWidget,
    handleTapReplace,
    handleTapSwap,
  } = useLayoutMode();

  // In VIEW mode, disable all DnD functionality
  const isEditMode = mode === ELayoutManagerMode.EDIT;

  // Check if awaiting placement (palette widget selected)
  const isAwaitingPlacement = isEditMode && selectedPaletteWidget !== null;

  // Memoize widget component lookup to prevent re-lookups on each render
  const WidgetComponent = useMemo(
    () => widgetRegistry.get(widget.type),
    [widget.type],
  );

  const handleClick = (e: React.MouseEvent) => {
    // No interaction in VIEW mode or while dragging
    if (!isEditMode || isDragging) {
      return;
    }

    if (widget.id !== 'new-cases-placeholder') {
      e.stopPropagation();

      // If a palette widget is selected, this is a tap-to-replace action
      if (selectedPaletteWidget) {
        handleTapReplace(widget.id, selectedPaletteWidget);
        return;
      }

      // Check if another widget is already selected for swap
      if (selectedWidgetId && selectedWidgetId !== widget.id) {
        handleTapSwap(selectedWidgetId, widget.id);
        return;
      }

      // If clicked on same widget, deselect
      if (selectedWidgetId === widget.id) {
        setSelectedWidgetId(null);
        return;
      }

      // Otherwise, just select this widget
      setSelectedWidgetId(widget.id);
      setSelectedPaletteWidget(null);
    }
  };

  return (
    <Root
      className={containerWidgetClasses.root}
      $isSelected={isSelected && isEditMode}
      $mode={mode}
      $isOver={isOver && isEditMode}
      $isAwaitingPlacement={isAwaitingPlacement}
      $isDragging={isDragging}
      $enableToolbarPointerEvents={
        mode !== ELayoutManagerMode.RENDER &&
        widget.id !== 'new-cases-aside' &&
        widget.id !== 'new-cases-placeholder'
      }
      onPointerUp={undefined}
      onClick={isEditMode ? handleClick : undefined}
    >
      <Box className={containerWidgetClasses.hoverZone} />
      <Box className={containerWidgetClasses.placeholder}>
        {WidgetComponent ? (
          <Box className={containerWidgetClasses.widgetWrapper}>
            <WidgetComponent
              instance={widget}
              showSettingsButton={
                mode !== ELayoutManagerMode.RENDER &&
                widget.id !== 'new-cases-aside' &&
                widget.id !== 'new-cases-placeholder'
              }
              outlined={false}
              preview={widget.type === 'geo-map' || widget.type === 'wind-map'}
            />
          </Box>
        ) : (
          // Fallback to widget type text if widget not found in registry
          widget.type
        )}
      </Box>
    </Root>
  );
});
